import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass, ArrowRight, ArrowLeft, ShieldAlert, CalendarClock,
  MessageCircleQuestion, AlertTriangle, Loader2, Save, Sparkles, Check, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { STATES, CHARGE_TYPES, ROADMAP_STAGES } from "@/data/legalData";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const YES_NO = [
  { v: "yes", label: "Yes" },
  { v: "no", label: "No" },
  { v: "unsure", label: "Not sure" },
];

const STEPS = [
  { key: "jurisdiction", q: "Where is the case being handled?", type: "choice",
    options: [{ v: "State", label: "State court" }, { v: "Federal", label: "Federal court" }, { v: "unsure", label: "Not sure" }] },
  { key: "state", q: "Which state are you in?", type: "state" },
  { key: "chargeType", q: "What kind of charge is it?", type: "select", options: CHARGE_TYPES },
  { key: "chargeLevel", q: "How serious is the charge?", type: "choice",
    options: [{ v: "Felony", label: "Felony" }, { v: "Misdemeanor", label: "Misdemeanor" }, { v: "unsure", label: "Not sure" }] },
  { key: "stage", q: "Where are you in the process right now?", type: "stage" },
  { key: "inCustody", q: "Are you (or the person) currently in custody?", type: "choice", options: YES_NO },
  { key: "hasAttorney", q: "Do you already have a lawyer?", type: "choice", options: YES_NO },
  { key: "firstOffense", q: "Is this a first offense?", type: "choice", options: YES_NO },
];

function buildPlan(a) {
  const stage = ROADMAP_STAGES.find((s) => s.id === a.stage) || ROADMAP_STAGES[0];
  const rightNow = [];
  const thisWeek = [];
  const watchOut = [...stage.risks];
  const questions = [...stage.questions];

  rightNow.push("Stay calm. You are presumed innocent — the government must prove its case.");
  if (a.inCustody === "yes") {
    rightNow.push("If you are being questioned, say clearly: 'I am invoking my right to remain silent and I want a lawyer.' Then stop talking.");
    rightNow.push("Assume all jail phone calls and visits are recorded — never discuss case details on them.");
  } else {
    rightNow.push("Do not discuss the details of your case with anyone except your lawyer — including on social media, texts, or calls.");
  }

  if (a.hasAttorney === "no" || a.hasAttorney === "unsure") {
    rightNow.push("Get a lawyer as your top priority. If you can't afford one, request a court-appointed public defender.");
    thisWeek.push("Check whether you qualify for a free public defender using the Eligibility Calculator.");
  } else {
    thisWeek.push("Schedule a meeting with your attorney and bring your written questions and timeline.");
  }

  thisWeek.push("Write down everything you remember about the events, in order, with dates and names — while it's fresh.");
  thisWeek.push("Gather documents, receipts, messages, or names of witnesses that could help your case.");
  thisWeek.push(`Read the '${stage.title}' stage of the Case Roadmap so you know what to expect next.`);

  if (a.chargeLevel === "Felony") {
    watchOut.push("Felony convictions carry long-term consequences (voting, employment, housing) — take every deadline seriously.");
  }
  if (a.jurisdiction === "Federal" || a.chargeType === "Federal charge") {
    watchOut.push("Federal cases move fast and have strict sentencing guidelines — an experienced federal defense attorney matters.");
  }
  if (a.firstOffense === "yes") {
    questions.push("Am I eligible for a diversion program, deferred adjudication, or expungement?");
  }
  questions.push("What are the realistic best-case and worst-case outcomes for a charge like mine?");

  // checklist to optionally save as a case
  const checklist = [...rightNow, ...thisWeek].map((text, i) => ({ id: `d${i}`, text, done: false }));

  return { stage, rightNow, thisWeek, watchOut, questions, checklist };
}

export default function Diagnostic() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = STEPS[step];
  const plan = done ? buildPlan(answers) : null;

  const setVal = (v) => setAnswers((a) => ({ ...a, [current.key]: v }));
  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : setDone(true));
  const back = () => (done ? setDone(false) : setStep(Math.max(0, step - 1)));
  const canNext = !!answers[current?.key];

  const restart = () => { setAnswers({}); setStep(0); setDone(false); };

  const exportPdf = () => {
    if (!plan) return;
    if (!user?.is_pro) {
      toast.error("Saving as PDF is a Case Pass feature.");
      navigate("/upgrade");
      return;
    }
    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    const section = (title, items) => `
      <h2>${esc(title)}</h2>
      <ul>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`;
    const summary = [
      ["Jurisdiction", answers.jurisdiction === "unsure" ? "Not sure" : answers.jurisdiction],
      ["State", answers.state === "unsure" ? "Not provided" : answers.state],
      ["Charge", answers.chargeType],
      ["Level", answers.chargeLevel === "unsure" ? "Not sure" : answers.chargeLevel],
      ["Current stage", plan.stage.title],
    ].filter(([, v]) => v);
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Case Diagnostic Plan — SentinelGuide</title>
      <style>
        body { font-family: Georgia, 'Times New Roman', serif; color:#161E1D; max-width:720px; margin:40px auto; padding:0 24px; line-height:1.55; }
        .hdr { border-bottom:3px solid #1B3B32; padding-bottom:16px; margin-bottom:20px; }
        .hdr h1 { font-size:28px; margin:0 0 4px; color:#112922; }
        .meta { font-size:12px; color:#5E716D; letter-spacing:.06em; text-transform:uppercase; font-family:Arial,sans-serif; }
        .facts { display:flex; flex-wrap:wrap; gap:8px; margin:0 0 22px; }
        .facts span { background:#F0F5F3; border:1px solid #E3EDE9; border-radius:8px; padding:6px 10px; font-size:12px; font-family:Arial,sans-serif; }
        h2 { font-size:15px; text-transform:uppercase; letter-spacing:.08em; color:#2F6557; margin:22px 0 8px; font-family:Arial,sans-serif; }
        ul { margin:0; padding-left:20px; } li { font-size:14.5px; margin:0 0 6px; }
        .disc { margin-top:28px; padding-top:14px; border-top:1px solid #DDD0B9; font-size:11px; color:#899A97; }
        @media print { body { margin:0; } }
      </style></head>
      <body>
        <div class="hdr"><h1>Case Diagnostic Plan</h1><div class="meta">Prepared with SentinelGuide · ${date}</div></div>
        <div class="facts">${summary.map(([k, v]) => `<span><strong>${esc(k)}:</strong> ${esc(v)}</span>`).join("")}</div>
        ${section("Do this right now", plan.rightNow)}
        ${section("This week", plan.thisWeek)}
        ${section("Questions for your lawyer", plan.questions)}
        ${section("Watch out for", plan.watchOut)}
        <div class="disc">General legal information only — NOT legal advice. Bring this to your attorney or public defender to discuss your specific situation.</div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to save your plan as a PDF."); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 400);
  };

  const saveAsCase = async () => {
    setSaving(true);
    try {
      await api.post("/cases", {
        title: `Diagnostic — ${answers.chargeType || "My case"}`,
        jurisdiction: answers.jurisdiction === "Federal" ? "Federal" : "State",
        state: answers.state === "unsure" ? "" : (answers.state || ""),
        lead_charge: answers.chargeType || "",
        charge_level: answers.chargeLevel === "unsure" ? "" : (answers.chargeLevel || ""),
        stage: answers.stage || "arrest",
        attorney: answers.hasAttorney === "yes" ? "Retained" : "",
        notes: "Created from the Case Diagnostic intake.",
        court_dates: [],
        checklist: plan.checklist,
      });
      toast.success("Saved to your Case Vault");
      navigate("/dashboard");
    } catch { toast.error("Could not save. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-forest-100 text-forest-800 mx-auto"><Compass className="h-7 w-7" /></span>
          <span className="mt-4 block font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Case Diagnostic</span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">A guided plan for your next steps</h1>
          <p className="mt-3 text-ink-700">Answer a few quick questions and get a tailored, plain-English checklist. General information only — not legal advice.</p>
        </div>

        {!done ? (
          <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="mt-10 rounded-3xl border border-parchment-200 bg-white p-8">
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-xs text-forest-600">Question {step + 1} of {STEPS.length}</span>
              <div className="h-1.5 w-40 rounded-full bg-parchment-200 overflow-hidden">
                <div className="h-full bg-forest-700 rounded-full transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            </div>
            <h2 className="font-serif text-2xl font-semibold text-forest-900">{current.q}</h2>

            <div className="mt-6">
              {(current.type === "choice") && (
                <div className="grid sm:grid-cols-3 gap-3">
                  {current.options.map((o) => (
                    <button key={o.v} data-testid={`diag-opt-${current.key}-${o.v}`} onClick={() => setVal(o.v)}
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        answers[current.key] === o.v ? "border-forest-800 bg-forest-50 ring-2 ring-forest-800/10" : "border-parchment-200 hover:border-forest-600/40"}`}>
                      <span className="flex items-center justify-between">
                        <span className="font-medium text-ink-900">{o.label}</span>
                        {answers[current.key] === o.v && <Check className="h-4 w-4 text-forest-700" />}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {current.type === "select" && (
                <Select value={answers[current.key] || ""} onValueChange={setVal}>
                  <SelectTrigger data-testid="diag-charge-select" className="rounded-xl border-parchment-200 h-12"><SelectValue placeholder="Choose the closest option" /></SelectTrigger>
                  <SelectContent>{current.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {current.type === "state" && (
                <Select value={answers[current.key] || ""} onValueChange={setVal}>
                  <SelectTrigger data-testid="diag-state-select" className="rounded-xl border-parchment-200 h-12"><SelectValue placeholder="Select your state" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="unsure">Not sure / prefer not to say</SelectItem>
                    {STATES.filter((s) => s.abbr !== "US").map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {current.type === "stage" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {ROADMAP_STAGES.map((s) => (
                    <button key={s.id} data-testid={`diag-stage-${s.id}`} onClick={() => setVal(s.id)}
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        answers.stage === s.id ? "border-forest-800 bg-forest-50 ring-2 ring-forest-800/10" : "border-parchment-200 hover:border-forest-600/40"}`}>
                      <span className="font-mono text-xs text-forest-600">STAGE {s.n}</span>
                      <p className="font-medium text-ink-900 mt-0.5">{s.title}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={back} disabled={step === 0} className="text-ink-500 gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
              <Button onClick={next} data-testid="diag-next-btn" disabled={!canNext} className="rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2 px-6">
                {step === STEPS.length - 1 ? "See my plan" : "Next"} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-10 space-y-5" data-testid="diagnostic-result">
            <PlanCard tone="urgent" icon={ShieldAlert} title="Do this right now" items={plan.rightNow} />
            <PlanCard tone="forest" icon={CalendarClock} title="This week" items={plan.thisWeek} />
            <div className="grid sm:grid-cols-2 gap-5">
              <PlanCard icon={MessageCircleQuestion} title="Questions for your lawyer" items={plan.questions} />
              <PlanCard tone="warn" icon={AlertTriangle} title="Watch out for" items={plan.watchOut} />
            </div>

            <div className="rounded-3xl bg-forest-900 text-parchment-50 p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl font-semibold">Keep this plan with you</h3>
                <p className="mt-1 text-parchment-200/80 text-sm">Save it to your private vault as a case with a ready-made checklist.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={exportPdf} data-testid="diag-export-pdf-btn" variant="outline" className="rounded-full border-parchment-50/30 bg-transparent text-parchment-50 hover:bg-parchment-50/10 gap-2">
                  <FileDown className="h-4 w-4" /> Save as PDF
                </Button>
                <Button onClick={() => navigate("/eligibility")} variant="outline" className="rounded-full border-parchment-50/30 bg-transparent text-parchment-50 hover:bg-parchment-50/10 gap-2">
                  <Sparkles className="h-4 w-4" /> Check lawyer eligibility
                </Button>
                <Button onClick={saveAsCase} data-testid="diag-save-case-btn" disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save to my vault
                </Button>
              </div>
            </div>

            <div className="text-center">
              <Button variant="ghost" onClick={restart} className="text-ink-500">Start over</Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

const PlanCard = ({ icon: Icon, title, items, tone }) => {
  const styles = {
    urgent: "border-l-4 border-terracotta bg-terracotta-subtle text-[#8C3415]",
    forest: "border border-parchment-200 bg-white text-ink-700",
    warn: "border-l-4 border-[#B45309] bg-[#FFFBEB] text-[#8a5a12]",
    default: "border border-parchment-200 bg-white text-ink-700",
  };
  const head = tone === "urgent" ? "text-[#8C3415]" : tone === "warn" ? "text-[#8a5a12]" : "text-forest-800";
  return (
    <div className={`rounded-2xl p-6 ${styles[tone] || styles.default}`}>
      <div className={`flex items-center gap-2 mb-3 ${head}`}>
        <Icon className="h-5 w-5" /><h3 className="font-semibold">{title}</h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="text-sm flex gap-3 leading-relaxed">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-current opacity-60 shrink-0" />{it}
          </li>
        ))}
      </ul>
    </div>
  );
};
